import { useEffect, useState } from "react";
import {
  PieChart,
  Pie, Cell, Tooltip as RechartsTooltip,
  BarChart, XAxis, YAxis,
  Legend, Bar, ResponsiveContainer,
} from "recharts";
import "./CBSEResultDashboard.css";
import { CHART_COLORS, CHART_STATUS_COLORS } from "../../styles/chartColors";

const COLORS = [
  CHART_STATUS_COLORS.positive, // Pass
  CHART_STATUS_COLORS.warning, // Compartment
  CHART_STATUS_COLORS.negative, // Essential Repeat
  CHART_STATUS_COLORS.neutral, // Others
];
const GENDER_COLORS = { boys: CHART_COLORS[4], girls: CHART_COLORS[3] };

const CBSEResultDashboard = ({ data, program_name }) => {
  const [dashboard, setDashboard] = useState(null);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [examInfo, setExamInfo] = useState(null);

  useEffect(() => {
    if (data) {
      setDashboard(data.dashboard);
      setSchoolInfo(data.school_info);
      setExamInfo(data.exam_info);
    }
  }, [data]);

  if (!dashboard || !schoolInfo || !examInfo) return <div></div>;

  const pieData = [
    { name: "Pass", value: 100 },
    { name: "Compartment", value: 0 },
    { name: "Essential Repeat", value: 0 },
    { name: "Others", value: 0 },
  ];

  const genderData = [
    { name: "Boys", value: dashboard.overview.boys },
    { name: "Girls", value: dashboard.overview.girls },
  ];

  // Define a color map for grades
  const GRADE_COLORS = {
    A1: CHART_STATUS_COLORS.positive,
    A2: CHART_STATUS_COLORS.positive,
    B1: CHART_COLORS[6], // lime
    B2: CHART_COLORS[6], // lime
    C1: CHART_STATUS_COLORS.warning,
    C2: CHART_STATUS_COLORS.warning,
    D: CHART_COLORS[7], // orange
    E1: CHART_STATUS_COLORS.negative,
    E2: CHART_STATUS_COLORS.negative,
    default: CHART_STATUS_COLORS.neutral
  };

  const percentageDistribution = Object.entries(
    dashboard.percentage_distribution
  ).map(([range, count]) => ({ range, count }));

  const avgMarksByGender = Object.keys(
    dashboard.subject_wise_average_marks.boys
  ).map((subject) => ({
    subject,
    boys: dashboard.subject_wise_average_marks.boys[subject],
    girls: dashboard.subject_wise_average_marks.girls[subject],
  }));

  const avgMarksCombined = Object.keys(
    dashboard.subject_wise_average_marks.overall
  ).map((subject) => ({
    subject,
    average: dashboard.subject_wise_average_marks.overall[subject],
  }));

  const subjectWiseTopperEntries = Object.entries(
    dashboard.subject_wise_toppers
  );

  const subjectWiseGradeDistributionEntries = Object.entries(
    dashboard.subject_wise_grade_distribution || {}
  );

  return (
    <div className="cbse-dashboard">
      <h2>🏫 School: {schoolInfo.name}</h2>
      <div className="dashboard-header mt-2">
        <h3>🧾 {examInfo.exam_name}</h3>
        <p>
          📅 {examInfo.date} | 🕒 {examInfo.generated_at}
        </p>
      </div>

      <h3>📊 CBSE {program_name === "10" ? "Class 10" : null || program_name === "12" ? "Class 12" : null} - Full Graphic View Dashboard</h3>

      <div className="overview-cards mt-3">
        <div className="cbse-card">
          <strong>Total Students</strong>
          <p>{dashboard.overview.total_students}</p>
        </div>
        <div className="cbse-card">
          <strong>Pass Percentage</strong>
          <p>{dashboard.overview.pass_percentage}</p>
        </div>
        <div className="cbse-card">
          <strong>Boys vs Girls</strong>
          <p>
            {dashboard.overview.boys} Boys | {dashboard.overview.girls} Girls
          </p>
        </div>
      </div>

      <div className="charts-section">
        <div className="chart-box">
          <h4>🎯 Pass / Compartment / Repeat</h4>
          <PieChart width={300} height={300}>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              outerRadius={100}
              label
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <RechartsTooltip />
          </PieChart>
        </div>

        <div className="chart-box">
          <h4>🧒 Boys vs 👧 Girls</h4>
          <PieChart width={300} height={300}>
            <Pie
              data={genderData}
              dataKey="value"
              nameKey="name"
              outerRadius={100}
              label
            >
              {genderData.map((entry, index) => (
                <Cell
                  key={`cell-gender-${index}`}
                  fill={
                    entry.name === "Boys"
                      ? GENDER_COLORS.boys
                      : GENDER_COLORS.girls
                  }
                />
              ))}
            </Pie>
            <RechartsTooltip />
          </PieChart>
        </div>

        <div className="chart-box">
          <h4>📈 Percentage Distribution</h4>
          <BarChart width={500} height={300} data={percentageDistribution}>
            <XAxis dataKey="range" />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Bar dataKey="count" fill={CHART_COLORS[7]} />
          </BarChart>
        </div>
      </div>

      <div className="bar-chart-section">
        <h3>📊 Subject-wise Average Marks (Boys vs Girls)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={avgMarksByGender}>
            <XAxis
              dataKey="subject"
              interval={0}
              angle={-30}
              textAnchor="end"
              height={80}
            />
            <YAxis />
            <Legend />
            <RechartsTooltip />
            <Bar dataKey="boys" fill={GENDER_COLORS.boys} name="Boys" />
            <Bar dataKey="girls" fill={GENDER_COLORS.girls} name="Girls" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bar-chart-section">
        <h3>📊 Subject-wise Overall Average Marks (Combined)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={avgMarksCombined}>
            <XAxis
              dataKey="subject"
              interval={0}
              angle={-30}
              textAnchor="end"
              height={80}
            />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Bar dataKey="average" fill={CHART_COLORS[7]} name="Average Marks" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="top-students">
        <h3>🏆 Top 10 Students (Best Five Subjects)</h3>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Roll Number</th>
              <th>Name</th>
              <th>Best Five Total</th>
              <th>Best Five Percentage</th>
            </tr>
          </thead>
          <tbody>
            {dashboard.top_10_students.map((student, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{student.roll_no}</td>
                <td>{student.name}</td>
                <td>{student.best_five_total}</td>
                <td>{student.best_five_percentage}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="subject-wise-toppers">
        <h3>📚 Subject-wise Toppers</h3>
        {subjectWiseTopperEntries.map(([subjectKey, data], i) => (
          <div key={i} className="subject-topper">
            <h4>🥇 {subjectKey} – Top Score: {data.top_score}</h4>
            <table>
              <thead>
                <tr>
                  <th>SrNo.</th>
                  <th>Roll Number</th>
                  <th>Name</th>
                  <th>Gender</th>
                  <th>Marks</th>
                </tr>
              </thead>
              <tbody>
                {data.students && data.students.map((student, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{student.roll_no}</td>
                    <td>{student.name}</td>
                    <td>{student.gender}</td>
                    <td>{data.top_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div className="subject-wise-grades">
        <h3>📉 Subject-wise Grade Distribution</h3>
        <div className="grades-grid">
          {subjectWiseGradeDistributionEntries.map(([subject, grades], index) => {
            const data = Object.entries(grades).map(([grade, { boys, girls }]) => ({
              grade,
              students: boys + girls
            }));

            return (
              <div className="grade-chart" key={index}>
                <h4>{subject} Grades</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data}>
                    <XAxis dataKey="grade" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="students">
                      {data.map((entry, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={GRADE_COLORS[entry.grade] || GRADE_COLORS.default}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default CBSEResultDashboard;
